import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ListChecks } from "@phosphor-icons/react";
import api from "@/lib/api";

function ApiNote({ url, statuses }) {
  return (
    <div className="mx-4 mb-3 border border-black bg-zinc-50 p-3 text-[11px]" data-testid="buyer-status-api-note">
      <b>Your system can send these automatically.</b> Post to <span className="break-all font-mono">{url}</span> with your key in the
      {" "}<span className="font-mono">X-Api-Key</span> header and a JSON body: <span className="font-mono">{"{ \"lead_id\": \"…\", \"status\": \"…\", \"notes\": \"…\" }"}</span>.
      {" "}Use one of these statuses or your own word: {statuses.map((s) => s.label).join(", ")}.
      {" "}Ask your account manager for your key.
    </div>
  );
}

function StatusRow({ row, statuses, busy, onSet }) {
  const s = row.status;
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-zinc-200 px-4 py-2 text-sm last:border-b-0" data-testid={`buyer-status-row-${row.lead_id}`}>
      <div className="min-w-[10rem] flex-1">
        <b>{row.consumer_name || "Lead"}</b>
        {row.is_test && <span className="ml-1 text-[10px] uppercase text-amber-800">test</span>}
        <div className="font-mono text-[10px] text-zinc-500">{row.state || "—"} · {new Date(row.delivered_at).toLocaleDateString()} · {row.lead_id.slice(0, 8)}</div>
      </div>
      {row.can_edit ? (
        <select value={s?.status || ""} disabled={busy} onChange={(e) => onSet(row, e.target.value)}
                className="border border-black px-2 py-1 text-xs" data-testid={`buyer-status-select-${row.lead_id}`}>
          <option value="" disabled>No status yet</option>
          {statuses.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
        </select>
      ) : (
        <span className="border border-black px-1.5 py-0.5 text-[10px] uppercase tracking-widest" data-testid={`buyer-status-label-${row.lead_id}`}>{s?.label || "No status yet"}</span>
      )}
      {s && <span className="w-full text-[10px] text-zinc-500 sm:w-auto">{s.source === "api" ? "from your system" : "set here"} · {new Date(s.at).toLocaleString()}</span>}
    </div>
  );
}

// The buyer's delivered leads (last 30 days) with the status they've sent back for each.
export function BuyerLeadStatuses() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = useCallback(() => {
    api.get("/buyer/lead-statuses").then((r) => setD(r.data)).catch(() => setD({ rows: [], statuses: [] }));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!d) return null;
  const onSet = async (row, status) => {
    setBusy(row.lead_id);
    try {
      await api.post(`/buyer/leads/${row.lead_id}/status`, { status });
      toast.success("Status saved");
      load();
    } catch (e) {
      const m = e?.response?.data?.detail;
      toast.error(typeof m === "string" ? m : "Couldn't save the status");
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="mt-8 brut-card" data-testid="buyer-lead-statuses">
      <div className="flex flex-wrap items-center gap-2 border-b border-black px-4 py-3">
        <ListChecks size={14} weight="bold" />
        <div className="font-chivo font-black uppercase tracking-tighter">Lead statuses</div>
        <div className="label-mono text-zinc-500">Last 30 days</div>
      </div>
      <p className="px-4 py-3 text-[11px] text-zinc-600">Tell us what happened with each lead, like Contacted, Signed or Not a case. It helps us send you better leads.</p>
      {d.api_enabled && <ApiNote url={d.api_url} statuses={d.statuses} />}
      {d.rows.length > 0 && !d.rows.some((r) => r.can_edit) && (
        <p className="px-4 pb-3 text-[11px] text-zinc-600" data-testid="buyer-status-api-only">Your system sends these statuses, so they can&apos;t be changed here.</p>
      )}
      {d.rows.length ? d.rows.map((r) => <StatusRow key={r.lead_id} row={r} statuses={d.statuses} busy={busy === r.lead_id} onSet={onSet} />) : (
        <div className="px-4 pb-4 text-center label-mono" data-testid="buyer-lead-statuses-empty">No leads delivered in the last 30 days.</div>
      )}
    </div>
  );
}
